import { useEffect, useState } from "react"
import { Alert, Form, Input } from "antd"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { AxiosError } from "axios"
import { useAuth } from "../../../components/contexts/AuthContext"
import PasswordRequirementsList, { passwordConfirmFieldRules, passwordFieldRules } from "../../../components/elements/PasswordRequirementsList"
import PasswordPageLayout from "../components/PasswordPageLayout"
import { postPasswordReset } from "../api/apiCalls"
import { readTokenFromHash } from "../utils/readToken"
import { apiErrorText } from "../../../services/apiErrorText"

type TPasswordSetForm = {
    password: string
    confirmPassword: string
}

const INVALID_LINK_TEXT = "Der Link ist ungültig oder abgelaufen."

type TResetErrorBody = {
    message?: string
    displayMessage?: string
    // Validierungsfehler (zod, validationErrorBody im Backend): { message: "Validation failed", displayMessage, details: [{ path, message }] }
    details?: { path?: string; message?: string }[]
}

export const resetErrorText = (error: unknown) => {
    const response = (error as AxiosError<TResetErrorBody>)?.response
    const body = response?.data
    if (response?.status === 401) return INVALID_LINK_TEXT
    if (response?.status === 400) {
        const details = body && Array.isArray(body.details) ? body.details : []
        // Token fehlt oder hat ein ungültiges Format -> wie ein ungültiger Link behandeln
        if (details.some((issue) => issue.path === "token")) return INVALID_LINK_TEXT
        if (details.length > 0 || body?.message === "Validation failed") return apiErrorText(error, "Ungültige Eingabedaten")
        // fachlicher 400 (Token ungültig, abgelaufen oder schon benutzt)
        return INVALID_LINK_TEXT
    }
    if (response?.status === 429 && !body?.displayMessage) return "Zu viele Versuche. Bitte warten Sie einen Moment und versuchen Sie es erneut."
    return apiErrorText(error, "Das Passwort konnte nicht gesetzt werden. Bitte versuchen Sie es später erneut.")
}

/**
 * `/passwort/neu#t=<token>` (Einladung und „Passwort vergessen“).
 * Der Token wird einmal aus dem Fragment gelesen und sofort aus der Adresszeile entfernt; er steht danach nur im State und im Body des Reset-Requests.
 */
export default function PasswordSet() {
    const auth = useAuth()
    const location = useLocation()
    const navigate = useNavigate()
    // reines Lesen im Initializer; das Entfernen passiert im Effect direkt nach dem ersten Rendern (vor jedem Request mit Token)
    const [token] = useState(() => readTokenFromHash(location.hash))
    const [form] = Form.useForm<TPasswordSetForm>()
    const password = Form.useWatch("password", form) ?? ""
    const confirmPassword = Form.useWatch("confirmPassword", form) ?? ""
    const [submitting, setSubmitting] = useState(false)
    const [showRequirementErrors, setShowRequirementErrors] = useState(false)
    const [errorText, setErrorText] = useState<string | undefined>(undefined)
    const [done, setDone] = useState(false)

    // Fragment über den Router entfernen (ersetzt den History-Eintrag): danach steht der Token weder in der Adresszeile noch in useLocation().hash
    useEffect(() => {
        if (location.hash) navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: location.state })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onFinish = async (values: TPasswordSetForm) => {
        if (!token) return
        setSubmitting(true)
        setErrorText(undefined)
        try {
            await postPasswordReset(token, values.password)
            setDone(true)
        } catch (error) {
            setErrorText(resetErrorText(error))
        } finally {
            setSubmitting(false)
        }
    }

    if (done)
        return (
            <PasswordPageLayout title="Passwort gesetzt">
                <Alert type="success" showIcon message="Ihr Passwort wurde gesetzt. Sie können sich jetzt anmelden." />
                {auth.isAuthenticated ? (
                    // in diesem Browser ist noch jemand angemeldet (z. B. Einladungslink geöffnet): erst abmelden, dann zur Anmeldung
                    <button type="button" className="btn btn-primary w-full" onClick={() => void auth.logout()}>
                        Zur Anmeldung
                    </button>
                ) : (
                    <Link to="/login" className="btn btn-primary w-full">
                        Zur Anmeldung
                    </Link>
                )}
            </PasswordPageLayout>
        )

    if (!token)
        return (
            <PasswordPageLayout title="Passwort setzen">
                <Alert type="error" showIcon message={INVALID_LINK_TEXT} />
                <RequestNewLink />
            </PasswordPageLayout>
        )

    return (
        <PasswordPageLayout title="Passwort setzen">
            {errorText && <Alert type="error" showIcon message={errorText} />}
            {errorText === INVALID_LINK_TEXT && <RequestNewLink />}
            <Form<TPasswordSetForm> form={form} layout="vertical" requiredMark={false} onFinish={onFinish} onFinishFailed={() => setShowRequirementErrors(true)} disabled={submitting}>
                <Form.Item
                    label="Neues Passwort"
                    name="password"
                    rules={passwordFieldRules}>
                    <Input.Password autoComplete="new-password" autoFocus size="large" />
                </Form.Item>
                <Form.Item
                    label="Passwort wiederholen"
                    name="confirmPassword"
                    dependencies={["password"]}
                    rules={passwordConfirmFieldRules("password")}>
                    <Input.Password autoComplete="new-password" size="large" />
                </Form.Item>
                <PasswordRequirementsList password={password} confirmPassword={confirmPassword} emphasizeUnmet={showRequirementErrors} />
                <button type="submit" className="btn btn-primary w-full" disabled={submitting}>
                    {submitting && <span className="loading loading-spinner" />}
                    Passwort speichern
                </button>
            </Form>
        </PasswordPageLayout>
    )
}

function RequestNewLink() {
    return (
        <div className="text-center">
            <Link to="/passwort/vergessen" className="text-primary hover:underline">
                Neuen Link anfordern
            </Link>
        </div>
    )
}
